import { ICommonSettings, IAzureOpenAISetting } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigAzureOpenAISettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingAzureOpenAI?: IAzureOpenAISetting;
}

export interface IConfigAzureOpenAISettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingAzureOpenAIId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigAzureOpenAISettings extends React.PureComponent<
  IConfigAzureOpenAISettingsProps,
  IConfigAzureOpenAISettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigAzureOpenAISettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle"
    };
  }

  componentDidMount() {
    this.loadAzureOpenAISetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigAzureOpenAISettingsProps) {
    if (prevProps.editingAzureOpenAIId !== this.props.editingAzureOpenAIId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadAzureOpenAISetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadAzureOpenAISetting();
    }
  }

  loadAzureOpenAISetting = () => {
    if (!this.props.editingAzureOpenAIId) return;

    const editingAzureOpenAI = this.props.commonSettings.azureOpenAISettings?.find(
      (x) => x.id === this.props.editingAzureOpenAIId && !x.isGlobalCommonConfigOnProjectConfig
    );

    if (!editingAzureOpenAI) return;
    this.setState({ editingAzureOpenAI }, () => {
      this.formRef.current?.resetFields();
      this.formRef.current?.setFieldsValue(editingAzureOpenAI);
    });
  };

  existingNameValidator = async (_rule, value) => {
    const azureOpenAIsSettings = this.props.commonSettings?.azureOpenAISettings;

    const isSettingAlreadyExist =
      azureOpenAIsSettings?.some(
        (x) => x.name === value && x.id !== this.props.editingAzureOpenAIId && !x.isGlobalCommonConfigOnProjectConfig
      ) || value === "Default";

    if (isSettingAlreadyExist) throw new Error("Azure Open AI Settings Name is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();

    let obj: IAzureOpenAISetting;
    if (this.props.editingAzureOpenAIId) {
      obj = {
        id: this.props.editingAzureOpenAIId,
        name: data.name,
        apiKey: data.apiKey,
        endpoint: data.endpoint?.trim(),
        deploymentName: data.deploymentName?.trim(),
        apiVersion: data.apiVersion?.trim()
      };
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "azureOpenAISettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      obj = {
        id: Guid.create().toString(),
        name: data.name,
        apiKey: data.apiKey?.trim(),
        endpoint: data.endpoint?.trim(),
        deploymentName: data.deploymentName?.trim(),
        apiVersion: data.apiVersion?.trim()
      };
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "azureOpenAISettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteAzureOpenAI = () => {
    if (!this.props.editingAzureOpenAIId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingAzureOpenAIId || "",
      configKey: "azureOpenAISettings",
      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Azure Open AI setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Azure Open AI Setting"
        secondHeader="Azure Open AI Setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingAzureOpenAIId ? this.deleteAzureOpenAI : undefined}
            submitLabel={this.props.editingAzureOpenAIId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configAzureOpenaiForm"
          />
        }
      >
        <Form
          id="configAzureOpenaiForm"
          name="azureOpenAISetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
          initialValues={{ apiVersion: "2024-10-21" }}
        >
          <ConfigModalBody
            description={this.props.getSectionDescription("appsettings_azureopenaisettingpop_up_description")}
          >
            <ConfigField label="Name" required tooltipContentKey="appsettings_azureopenaisettingpop_up_name">
              <Form.Item
                name="name"
                rules={[
                  { required: true, message: "Please enter a Azure Open AI Setting Name." },
                  { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Azure Open AI Setting Name is invalid" },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField label="Endpoint" required tooltipContentKey="appsettings_azureopenaisettingpop_up_endpoint">
              <Form.Item
                name="endpoint"
                rules={[
                  { required: true, message: "Please enter a Endpoint URL." },
                  {
                    pattern: /^https?:\/\/.+/i,
                    message: "Endpoint must start with http:// or https://"
                  }
                ]}
              >
                <KuikaInput size="md" placeholder="https://<resource>.openai.azure.com/" />
              </Form.Item>
            </ConfigField>
            <ConfigField label="Api Key" required tooltipContentKey="appsettings_azureopenaisettingpop_up_apikey">
              <Form.Item name="apiKey" rules={[{ required: true, message: "Please enter a Api Key." }]}>
                <KuikaInput size="md" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Deployment Name"
              required
              tooltipContentKey="appsettings_azureopenaisettingpop_up_deploymentname"
            >
              <Form.Item name="deploymentName" rules={[{ required: true, message: "Please enter a Deployment Name." }]}>
                <KuikaInput size="md" placeholder="vision-prod" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Api Version"
              required
              tooltipContentKey="appsettings_azureopenaisettingpop_up_apiversion"
            >
              <Form.Item name="apiVersion" rules={[{ required: true, message: "Please enter a Api Version." }]}>
                <KuikaInput size="md" placeholder="2024-10-21" />
              </Form.Item>
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
